// src/components/Auth/ResetPasswordForm.jsx
import React, { useState, useCallback, useMemo } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { authRateLimiter } from '../../utils/rateLimiter';

const ResetPasswordForm = ({ onLoginClick }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Memoize email validation regex (same as RegisterForm)
  const emailRegex = useMemo(() => 
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    []
  );

  // Memoize rate limit options
  const rateLimitOptions = useMemo(() => ({
    maxAttempts: 3,
    windowMs: 60 * 60 * 1000
  }), []);

  // Email validation function
  const validateEmail = useCallback((emailToValidate) => {
    return emailRegex.test(String(emailToValidate).toLowerCase());
  }, [emailRegex]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    
    // Validate email format first
    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }
    
    setLoading(true);

    try {
      // Check rate limiting for password reset attempts
      if (!authRateLimiter.check(email, 'password-reset', rateLimitOptions)) {
        throw new Error("Too many password reset attempts. Please try again later.");
      }

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin + '/reset-password',
      });

      if (error) throw error;
      
      setMessage('Password reset instructions sent to your email!');
      
      // Reset rate limiter on success
      authRateLimiter.reset(email, 'password-reset');
    } catch (err) {
      console.error('Reset password error:', err);
      setError(err.message || 'Failed to send reset instructions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [email, validateEmail, rateLimitOptions]);

  const handleEmailChange = useCallback((e) => {
    setEmail(e.target.value);
  }, []);

  const handleLoginClick = useCallback(() => {
    onLoginClick();
  }, [onLoginClick]);

  // Memoize loading button content
  const loadingContent = useMemo(() => {
    if (!loading) return 'Send Reset Instructions';
    
    return (
      <div className="flex items-center">
        <svg className="animate-spin mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span>Sending...</span>
      </div>
    );
  }, [loading]);

  return (
    <div className="px-8 py-10">
      <h2 className="text-2xl font-bold text-sws-navy mb-2">Reset Your Password</h2>
      <p className="text-sws-slate mb-6">We'll send you instructions to reset your password</p>
      
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
          <p>{error}</p>
        </div>
      )}
      
      {message && (
        <div className="bg-green-50 border-l-4 border-green-500 text-green-700 p-4 mb-6 rounded">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium">{message}</p>
              <p className="text-xs mt-1">Check your inbox and spam folder for the reset email.</p>
            </div>
          </div>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="reset-email">
            Email Address
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-sws-slate" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
              </svg>
            </div>
            <input
              id="reset-email"
              type="email"
              value={email}
              onChange={handleEmailChange}
              className="pl-10 w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sws-purple focus:border-transparent transition-colors"
              placeholder="you@example.com"
              required
            />
          </div>
        </div>
        
        <div className="pt-4">
          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-sws-navy hover:bg-sws-slate focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sws-purple transition-colors"
          >
            {loadingContent}
          </button>
        </div>
      </form>
      
      <div className="mt-8 text-center">
        <button
          onClick={handleLoginClick}
          className="inline-flex items-center text-sm font-medium text-sws-purple hover:text-sws-navy transition-colors"
        >
          <svg className="mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to Sign In
        </button>
      </div>
      
      <div className="mt-10 border-t border-gray-200 pt-6">
        <div className="rounded-md bg-blue-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1 md:flex md:justify-between">
              <p className="text-xs text-blue-700">
                Didn't receive a reset email? Check your spam folder or contact support.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ResetPasswordForm);